import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Coins, Loader2, Lock, ShieldCheck, Sparkles, Star, X } from "lucide-react";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { api, C, errorMessage, fmtChips } from "../../lib/api";
import { AssetImage } from "../AssetImage";
import { TimeBankCard } from "../TimeBankCard";
import { sounds } from "../../lib/sounds";

export const fmtAud = (cents) => `A$${(cents / 100).toFixed(2)}`;
export const chipArt = (chips) => (chips >= 1000000 ? "chip_1m" : chips >= 250000 ? "chip_250k" : chips >= 100000 ? "chip_100k" : chips >= 50000 ? "chip_50k" : chips >= 25000 ? "chip_25k" : "chip_5k");
export const smallestCovering = (packages, need) => { const chips = packages.filter((p) => p.kind !== "timebank"); return chips.find((p) => p.chips >= need) || chips[chips.length - 1]; };

const stripeCache = {};
const stripeFor = (key) => (stripeCache[key] = stripeCache[key] || loadStripe(key));

// Our own chip art: each pile is a different three-dimensional cluster, and the display height grows with the
// package so a A$100 buy visibly towers over a A$10 one.
// NOTE: nginx caches /assets for 30 days, so REDRAWN ART MUST GET A NEW FILENAME (bump the -vN suffix) or browsers
// will keep serving the old picture for a month.
const STACK_ART = {
  ten: { src: "/assets/store/ten-v2.png", h: 64 },
  twenty: { src: "/assets/store/twenty-v2.png", h: 78 },
  fifty: { src: "/assets/store/fifty-v2.png", h: 90 },
  hundred: { src: "/assets/store/hundred-v2.png", h: 104 },
};
const artFor = (p) => STACK_ART[p.package_id]
  || (p.chips >= 150000 ? STACK_ART.hundred : p.chips >= 70000 ? STACK_ART.fifty : p.chips >= 25000 ? STACK_ART.twenty : STACK_ART.ten);

export const ChipStack = ({ pkg, scale = 1 }) => {
  const art = artFor(pkg);
  return <img src={art.src} alt="" aria-hidden="true" style={{ height: Math.round(art.h * scale) }}
    className="w-auto object-contain object-bottom drop-shadow-[0_8px_16px_rgba(0,0,0,0.7)]" />;
};

// What the package throws in on top of the chips — same gold as the rest of the room.
const Extras = ({ p }) => (
  <div className="flex flex-col items-center gap-1">
    {p.vip > 0 && (
      <div className="flex items-center gap-1.5 rounded-full border border-amber-400/50 bg-amber-400/10 px-2 py-0.5">
        <Star className="w-3 h-3 text-amber-300 fill-amber-300" />
        <span className="text-[10px] font-mono text-amber-200">{p.vip} VIP</span>
      </div>
    )}
    {p.cards > 0 && p.kind !== "timebank" && (
      <div className="flex items-center gap-1.5 rounded-full border border-amber-400/50 bg-amber-400/10 pl-1 pr-2 py-0.5">
        <TimeBankCard h={13} />
        <span className="text-[10px] font-mono text-amber-200">{p.cards} time card{p.cards > 1 ? "s" : ""}</span>
      </div>
    )}
  </div>
);

// Placeholders beside the time bank pack so the bottom row stays balanced until there is more to sell.
const ComingSoon = ({ i }) => (
  <div data-testid={`coming-soon-${i}`} aria-hidden="true"
    className="h-full rounded-2xl border border-dashed border-white/10 bg-black/20 p-4 flex flex-col items-center justify-center gap-2 text-center select-none">
    <Sparkles className="w-7 h-7 text-slate-600" />
    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Coming soon</div>
  </div>
);

const PackageCard = ({ p, highlight, onBuy }) => (
  <button data-testid={`package-${p.package_id}`} onClick={() => onBuy(p)}
    className={`relative h-full text-left rounded-2xl border p-4 flex flex-col items-center gap-2 btn-lift transition-colors ${highlight ? "border-amber-400 bg-amber-400/10 shadow-[0_0_30px_rgba(251,191,36,0.35)]" : "border-white/10 bg-black/40 hover:border-amber-300/50"}`}>
    {p.badge && <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-amber-400 text-slate-900 text-[9px] font-display tracking-wider px-2 py-0.5 whitespace-nowrap">{p.badge.toUpperCase()}</span>}
    <div className="h-[108px] flex items-end justify-center" data-testid={`package-art-${p.package_id}`}>
      {p.kind === "timebank"
        ? <div className="relative h-[72px] w-[116px]">
            {[0, 1, 2].map((i) => (
              <div key={i} className="absolute bottom-0" style={{ left: i * 18, zIndex: i, transform: `rotate(${(i - 1) * 6}deg)`, transformOrigin: "bottom center" }}>
                <TimeBankCard h={58} />
              </div>
            ))}
          </div>
        : <ChipStack pkg={p} />}
    </div>
    <div className="font-display text-sm text-amber-50">{p.name}</div>
    <div className="font-mono text-lg text-amber-100 leading-none">{p.kind === "timebank" ? `${p.cards} cards` : fmtChips(p.chips)}</div>
    {p.kind === "timebank" && <div className="text-[10px] text-slate-400 text-center leading-tight">Each adds a full clock to your turn · max {C.TDA.TIMEBANK_MAX_ACCOUNT} per account</div>}
    <Extras p={p} />
    {p.bonus_pct > 0 && <div className="text-[10px] uppercase tracking-wider text-emerald-300">+{p.bonus_pct}% bonus included</div>}
    <div className="mt-auto pt-3">
      <div className="rounded-full btn-gold px-4 h-7 flex items-center justify-center text-xs font-display tracking-wider leading-none">{fmtAud(p.price_cents)}</div>
    </div>
  </button>
);

// Chip store, fully inside the platform: packages → pay (Stripe's card form embedded right here) → finalising → done.
// `after`: "lobby" sends the player to the lobby when done (buy-in shortfall flow); "stay" closes the overlay where they are.
export const StoreFlow = ({ origin = "lobby", after = "stay", need = 0, startWith = null, onClose, onPurchased }) => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [stage, setStage] = useState(startWith ? "loading" : "packages"); // packages | pay | finalising | done | failed
  const [txn, setTxn] = useState(null);
  const [secret, setSecret] = useState(null);
  const [err, setErr] = useState(null);
  const started = useRef(false);
  useEffect(() => { api.get(C.ApiRoutes.STORE_PACKAGES).then((r) => setData(r.data)).catch((e) => setErr(errorMessage(e))); }, []);
  const buy = async (p) => {
    try {
      const { data: t } = await api.post(C.ApiRoutes.STORE_CHECKOUT, { package_id: p.package_id, origin });
      setTxn(t); setStage("pay"); setSecret(null);
      if (!data?.test_mode) {
        const { data: s } = await api.post(C.ApiRoutes.STORE_TXN_SESSION.replace("{txn_id}", t.txn_id));
        setSecret(s.client_secret);
      }
    } catch (e) { toast.error(errorMessage(e)); setStage("packages"); }
  };
  useEffect(() => { // shortfall popup handed us a package: go straight to the pay step
    if (!data || !startWith || started.current) return;
    started.current = true;
    const p = data.packages.find((x) => x.package_id === startWith);
    if (p) buy(p); else setStage("packages");
  }, [data, startWith]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancel = async () => {
    if (txn && stage === "pay") api.post(C.ApiRoutes.STORE_TXN_CANCEL.replace("{txn_id}", txn.txn_id)).catch(() => {});
    onClose();
  };
  const land = (t) => { setTxn(t); setStage("done"); sounds.chipsBet(); onPurchased?.(t); };
  // Stripe took the card: wait for the money to be confirmed before we claim anything was credited.
  const confirm = async () => {
    setStage("finalising");
    for (let i = 0; i < 40; i++) {
      try {
        const { data: t } = await api.get(C.ApiRoutes.STORE_TXN.replace("{txn_id}", txn.txn_id));
        if (t.status === "paid") return land(t);
        if (t.status === "failed") { setErr("Your bank declined the payment"); return setStage("failed"); }
      } catch { /* keep waiting — a blip shouldn't fail a paid order */ }
      await new Promise((r) => setTimeout(r, 1500));
    }
    setErr("We haven't had confirmation from the bank yet. Your chips will appear automatically once it clears.");
    setStage("failed");
  };
  const payNow = async () => { // TEST MODE only: no card, straight to credited
    setStage("finalising");
    try { land((await api.post(C.ApiRoutes.STORE_TXN_PAY.replace("{txn_id}", txn.txn_id))).data); }
    catch (e) { setErr(errorMessage(e)); setStage("failed"); }
  };
  useEffect(() => {
    if (stage !== "done") return undefined;
    const id = setTimeout(() => { if (after === "lobby") nav("/lobby"); onClose(); }, 3200);
    return () => clearTimeout(id);
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps
  const locked = stage === "finalising";
  return (
    <div data-testid="store-flow" data-stage={stage} className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center" onClick={locked ? undefined : cancel}>
      <div className="gold-card rounded-3xl w-[720px] max-w-[94vw] max-h-[92vh] overflow-y-auto scroll-thin rise" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-6 py-4 border-b border-amber-300/20">
          <div className="flex items-center gap-3">
            <Coins className="w-5 h-5 text-amber-400" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] gold-text">Chip store</div>
              <div className="text-[10px] text-slate-400">Play chips · entertainment only · never redeemable</div>
            </div>
            {data?.test_mode ? <span data-testid="store-test-mode" className="rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-200 text-[9px] font-display tracking-wider px-2 py-0.5">TEST MODE · no card charged</span>
              : data?.stripe_mode === "test" && <span data-testid="store-stripe-test" className="rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-200 text-[9px] font-display tracking-wider px-2 py-0.5">STRIPE TEST · card 4242 4242 4242 4242</span>}
          </div>
          {!locked && <button data-testid="store-dismiss" onClick={cancel} aria-label="Dismiss" className="w-8 h-8 rounded-md bg-red-600 hover:bg-red-500 text-white flex items-center justify-center btn-lift"><X className="w-4 h-4" /></button>}
        </header>

        {(stage === "loading" || (!data && !err)) && <div className="p-10 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        {err && stage !== "failed" && <div className="p-6 text-sm text-red-300">{err}</div>}

        {stage === "packages" && data && (
          <div className="p-6 flex flex-col gap-5">
            {need > 0 && <div data-testid="store-need" className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-sm text-amber-100">You're <span className="font-mono">{fmtChips(need)}</span> short for that buy-in — any highlighted package covers it.</div>}
            <div className="grid grid-cols-4 gap-3 items-stretch" data-testid="store-packages">
              {data.packages.filter((p) => p.kind !== "timebank").map((p) => <PackageCard key={p.package_id} p={p} highlight={need > 0 && p.chips >= need} onBuy={buy} />)}
            </div>
            {data.packages.some((p) => p.kind === "timebank") && (
              <div className="grid grid-cols-4 gap-3 items-stretch" data-testid="store-extras">
                {data.packages.filter((p) => p.kind === "timebank").map((p) => <PackageCard key={p.package_id} p={p} onBuy={buy} />)}
                {[0, 1, 2].map((i) => <ComingSoon key={i} i={i} />)}
              </div>
            )}
            <div className="text-[11px] text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Prices in AUD. Chips land in your wallet the moment the payment clears.</div>
          </div>
        )}

        {stage === "pay" && txn && (
          <div className="p-6 grid grid-cols-[1fr_1.2fr] gap-6" data-testid="store-pay">
            <div className="rounded-2xl bg-black/40 border border-white/10 p-5 flex flex-col items-center gap-3">
              {txn.kind === "timebank" ? <TimeBankCard h={96} />
                : <ChipStack pkg={txn.package} scale={0.85} />}
              <div className="font-display text-base text-amber-50">{txn.package.name}</div>
              <div className="font-mono text-2xl text-amber-100" data-testid="store-pay-chips">{txn.kind === "timebank" ? `${txn.cards} cards` : fmtChips(txn.chips)}</div>
              <Extras p={txn} />
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-1">Order total</div>
              <div className="font-mono text-xl text-emerald-300" data-testid="store-pay-total">{fmtAud(txn.amount_cents)}</div>
            </div>
            <div className="flex flex-col gap-3">
              <div className="text-[11px] uppercase tracking-[0.25em] gold-text flex items-center gap-2"><Lock className="w-3 h-3" /> Secure card payment</div>
              {data?.test_mode ? (
                <>
                  <div data-testid="payment-form-slot" className="rounded-xl border border-dashed border-amber-300/40 bg-black/30 p-4 text-xs text-slate-300">
                    Test mode: no card needed — PAY NOW credits everything so you can try the whole flow.
                  </div>
                  <p className="text-[11px] text-slate-500">You can dismiss this window at any time before PAY NOW.</p>
                  <div className="mt-auto flex gap-3">
                    <button data-testid="store-cancel" onClick={cancel} className="flex-1 rounded-full py-3 text-sm bg-white/5 hover:bg-white/10 btn-lift">Cancel</button>
                    <button data-testid="store-pay-now" onClick={payNow} className="flex-1 rounded-full py-3 text-sm font-display tracking-wider btn-gold btn-lift">PAY NOW · {fmtAud(txn.amount_cents)}</button>
                  </div>
                </>
              ) : secret && data?.publishable_key ? (
                <>
                  <div data-testid="payment-form-slot" className="rounded-xl overflow-hidden bg-black/30 border border-white/10">
                    <EmbeddedCheckoutProvider stripe={stripeFor(data.publishable_key)} options={{ clientSecret: secret, onComplete: confirm }}>
                      <EmbeddedCheckout className="min-h-[320px]" />
                    </EmbeddedCheckoutProvider>
                  </div>
                  <p className="text-[11px] text-slate-500">Your card details go straight to Stripe — we never see or store them. Dismiss any time before you pay.</p>
                  <button data-testid="store-cancel" onClick={cancel} className="rounded-full py-2.5 text-sm bg-white/5 hover:bg-white/10 btn-lift">Cancel</button>
                </>
              ) : (
                <div data-testid="payment-form-loading" className="rounded-xl border border-white/10 bg-black/30 p-10 flex flex-col items-center gap-3 text-xs text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                  Opening a secure payment…
                </div>
              )}
            </div>
          </div>
        )}

        {stage === "finalising" && (
          <div className="p-12 flex flex-col items-center gap-4 text-center" data-testid="store-finalising">
            <Loader2 className="w-10 h-10 text-amber-400 animate-spin" />
            <div className="font-display text-base text-amber-50">Confirming your payment…</div>
            <div className="text-xs text-slate-400">Please keep this window open. This only takes a moment.</div>
          </div>
        )}

        {stage === "done" && txn && (
          <div className="p-12 flex flex-col items-center gap-4 text-center" data-testid="store-done">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-400/50 flex items-center justify-center"><Check className="w-8 h-8 text-emerald-300" /></div>
            <div className="font-display text-lg text-amber-50">{txn.kind === "timebank" ? "Time bank cards added!" : "Chips credited!"}</div>
            <div className="font-mono text-3xl text-amber-100">{txn.kind === "timebank" ? `+${txn.cards} cards` : `+${fmtChips(txn.chips)}`}</div>
            {(txn.vip > 0 || (txn.cards > 0 && txn.kind !== "timebank")) && (
              <div className="flex gap-4 text-xs" data-testid="store-done-extras">
                {txn.vip > 0 && <span className="text-amber-300">+{txn.vip} VIP points</span>}
                {txn.cards > 0 && txn.kind !== "timebank" && <span className="text-amber-300">+{txn.cards} time card{txn.cards > 1 ? "s" : ""}</span>}
              </div>
            )}
            <div className="text-xs text-slate-400">{after === "lobby" ? "Taking you back to the lobby…" : "Back to the table…"}</div>
          </div>
        )}

        {stage === "failed" && (
          <div className="p-10 flex flex-col items-center gap-4 text-center" data-testid="store-failed">
            <div className="font-display text-base text-red-300">Payment didn't go through</div>
            <div className="text-xs text-slate-400">{err} — you have not been charged.</div>
            <button data-testid="store-failed-close" onClick={onClose} className="rounded-full px-6 py-2 text-sm bg-white/10 hover:bg-white/20">Close</button>
          </div>
        )}
      </div>
    </div>
  );
};
